/**
 * CustomerActivityFeed — "Since your last login" activity list for the
 * customer dashboard. Answers "What has changed since I was here last?"
 *
 * V1 derives everything from the already-loaded orders:
 *   - Compare each order's `updated_at` against `previousLastLogin`.
 *   - For orders that moved, infer the most recent change from the
 *     current state (status, artwork_status, invoice_status). This is
 *     coarse but faithful — we never invent events, only surface the
 *     visible state of the order.
 *
 * A dedicated order-events log will let us tell a richer story later,
 * but this V1 pattern already answers the "what's new?" question with
 * zero backend work.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../contexts/TranslationContext';
import { Card } from './ui/card';
import {
    Truck,
    Palette,
    FileText,
    ChevronRight,
    History,
    CheckCircle2,
    Package,
    CreditCard,
    Info,
} from 'lucide-react';

const parseIso = (v) => {
    if (!v) return null;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
};

const CustomerActivityFeed = ({ orders = [], previousLastLogin }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    // Collapse behaviour: default show max 2 activities. When there are
    // more than 2, a single "Show X more" toggle expands the rest;
    // clicking again collapses back to 2. ≤2 activities → no control.
    const [expanded, setExpanded] = useState(false);

    const relative = (date) => {
        if (!date) return '';
        const diffMs = Date.now() - date.getTime();
        const mins = Math.round(diffMs / 60000);
        if (mins < 1) return t('activity.just_now', 'just now');
        if (mins < 60) return t('activity.mins_ago', '{n} min ago', { n: mins });
        const hrs = Math.round(mins / 60);
        if (hrs < 24) return t('activity.hours_ago', '{n}h ago', { n: hrs });
        const days = Math.round(hrs / 24);
        if (days < 7) return t('activity.days_ago', '{n}d ago', { n: days });
        return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    };

    const summariseChange = (o) => {
        const status = o.status;

        // 1. Carrier tracking transitions win — they're the most
        //    meaningful "shipping" activity a customer can see, and we
        //    derive them from the flat rollup fields the tracking
        //    handler writes on the order (no separate event log).
        if (o.carrier_tracking_status === 'delivered') {
            return { icon: CheckCircle2, iconColor: 'text-emerald-600', iconBg: 'bg-emerald-50', label: t('activity.order_delivered', 'Order delivered') };
        }
        if (o.carrier_tracking_last_transition === 'out_for_delivery') {
            return { icon: Truck, iconColor: 'text-indigo-600', iconBg: 'bg-indigo-50', label: t('activity.out_for_delivery', 'Out for delivery') };
        }
        if (o.carrier_tracking_last_transition === 'at_dispatching_center') {
            return { icon: Truck, iconColor: 'text-indigo-600', iconBg: 'bg-indigo-50', label: t('activity.at_dispatching_center', 'Arrived at dispatching center') };
        }
        if (o.carrier_tracking_last_transition === 'in_transportation') {
            return { icon: Truck, iconColor: 'text-indigo-600', iconBg: 'bg-indigo-50', label: t('activity.shipment_in_transit', 'Shipment in transit') };
        }
        // ETA moved — surfaces "Delivery date updated" whenever the
        // effective carrier ETA changes without a status transition
        // (delayed, brought forward, or supplier date reissued).
        if (o.carrier_tracking_last_eta_change_at) {
            return { icon: Truck, iconColor: 'text-amber-600', iconBg: 'bg-amber-50', label: t('activity.delivery_date_updated', 'Delivery date updated') };
        }

        if (status === 'Delivered/Closed' || status === 'Completed') {
            return { icon: CheckCircle2, iconColor: 'text-emerald-600', iconBg: 'bg-emerald-50', label: t('activity.order_delivered', 'Order delivered') };
        }
        if (status === 'Shipping') {
            return { icon: Truck, iconColor: 'text-indigo-600', iconBg: 'bg-indigo-50', label: t('activity.order_shipped', 'Order shipped') };
        }
        if (status === 'In Production' || status === 'in_production') {
            return { icon: Package, iconColor: 'text-amber-600', iconBg: 'bg-amber-50', label: t('activity.moved_to_production', 'Moved to production') };
        }
        if (o.artwork_status === 'Awaiting_Approval') {
            return { icon: Palette, iconColor: 'text-purple-600', iconBg: 'bg-purple-50', label: t('activity.artwork_ready', 'New artwork ready for review') };
        }
        if (o.artwork_review_status === 'rejected') {
            return { icon: Palette, iconColor: 'text-rose-600', iconBg: 'bg-rose-50', label: t('activity.artwork_revision', 'Artwork revision requested') };
        }
        if (o.invoice_status === 'booked' || o.invoice_economic_status === 'booked_in_economic') {
            return { icon: CreditCard, iconColor: 'text-indigo-600', iconBg: 'bg-indigo-50', label: t('activity.invoice_created', 'Invoice created') };
        }
        if (o.invoice_status === 'paid') {
            return { icon: CreditCard, iconColor: 'text-emerald-600', iconBg: 'bg-emerald-50', label: t('activity.invoice_paid', 'Invoice paid') };
        }
        if (status === 'Submitted') {
            return { icon: FileText, iconColor: 'text-blue-600', iconBg: 'bg-blue-50', label: t('activity.order_submitted', 'Order submitted') };
        }
        // Fallback — translate the raw backend status value through
        // the shared ``order.status.*`` key family so Danish/other
        // locales never see a bare English status string here.
        const statusKeyMap = {
            'Draft':            'order.status.draft',
            'Quote':            'order.status.quote',
            'Quoted':           'order.status.quoted',
            'Submitted':        'order.status.submitted',
            'In Progress':      'order.status.in_progress',
            'In Production':    'order.status.in_production',
            'in_production':    'order.status.in_production',
            'On Hold':          'order.status.on_hold',
            'Shipping':         'order.status.shipping',
            'Completed':        'order.status.completed',
            'Delivered/Closed': 'order.status.delivered_closed',
            'Closed':           'order.status.closed',
            'Cancelled':        'order.status.cancelled',
        };
        const translatedStatus = status
            ? t(statusKeyMap[status] || 'order.status.generic', status)
            : t('activity.updated', 'Updated');
        return { icon: Info, iconColor: 'text-slate-500', iconBg: 'bg-slate-100', label: t('activity.status_generic', 'Status: {status}', { status: translatedStatus }) };
    };

    const cutoff = parseIso(previousLastLogin);

    // No previous login means this is the customer's first visit. Show
    // a friendly onboarding note instead of pretending there's activity.
    if (!cutoff) {
        return (
            <Card className="mb-4" data-testid="customer-activity-feed">
                <div className="px-4 py-3 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-slate-100 rounded-md">
                            <History className="h-4 w-4 text-slate-600" />
                        </div>
                        <div>
                            <h2 className="text-sm font-semibold text-slate-900 leading-tight">{t('activity.title', 'Since your last login')}</h2>
                            <p className="text-[11px] text-slate-500 leading-tight">{t('activity.subtitle', 'What has changed since I was here last?')}</p>
                        </div>
                    </div>
                </div>
                <div className="px-4 py-4 text-sm text-slate-500" data-testid="customer-activity-feed-first-visit">
                    {t('activity.first_visit', "Welcome back — this is your first visit, so there's no history to show yet. Next time you sign in, we'll list everything that has changed on your orders.")}
                </div>
            </Card>
        );
    }

    const changed = orders
        .map((o) => {
            // The order's own updated_at is the primary "moved since
            // last visit" signal, but tracking refreshes now write
            // dedicated timestamps (carrier_tracking_last_transition_at
            // / carrier_tracking_last_eta_change_at). Use the MAX of
            // the three so a tracking-only inbound push (which doesn't
            // bump order.updated_at because it lives on the supplier
            // confirmation) still surfaces in the feed.
            const updated = parseIso(o.updated_at || o.updatedAt);
            const transitionAt = parseIso(o.carrier_tracking_last_transition_at);
            const etaChangeAt = parseIso(o.carrier_tracking_last_eta_change_at);
            const effective = [updated, transitionAt, etaChangeAt]
                .filter(Boolean)
                .sort((a, b) => b - a)[0];
            if (!effective || effective <= cutoff) return null;
            return { order: o, updated: effective };
        })
        .filter(Boolean)
        .sort((a, b) => b.updated - a.updated)
        .slice(0, 8);

    return (
        <Card className="mb-4" data-testid="customer-activity-feed">
            <div className="px-4 py-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-slate-100 rounded-md">
                        <History className="h-4 w-4 text-slate-600" />
                    </div>
                    <div>
                        <h2 className="text-sm font-semibold text-slate-900 leading-tight">{t('activity.title', 'Since your last login')}</h2>
                        <p className="text-[11px] text-slate-500 leading-tight">
                            {t('activity.last_visit', 'Last visit {when}', { when: relative(cutoff) })}
                        </p>
                    </div>
                </div>
            </div>

            {changed.length === 0 ? (
                <div className="px-4 py-4 flex items-center gap-3 text-sm text-slate-600" data-testid="customer-activity-feed-empty">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                    <div>
                        <p className="font-medium text-slate-900">{t('activity.no_changes_title', 'No changes since your last visit.')}</p>
                        <p className="text-[11px] text-slate-500">
                            {t('activity.no_changes_hint', 'Orders will appear here as they progress.')}
                        </p>
                    </div>
                </div>
            ) : (
                <>
                <ul className="divide-y divide-slate-100">
                    {(expanded ? changed : changed.slice(0, 2)).map(({ order, updated }) => {
                        const c = summariseChange(order);
                        const Icon = c.icon;
                        return (
                            <li key={order.id}>
                                <button
                                    type="button"
                                    onClick={() => navigate(`/orders/${order.id}`)}
                                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors"
                                    data-testid={`activity-${order.id}`}
                                >
                                    <div className={`p-1.5 rounded-md ${c.iconBg} flex-shrink-0`}>
                                        <Icon className={`h-4 w-4 ${c.iconColor}`} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-slate-900 leading-tight truncate">
                                            {c.label}
                                        </p>
                                        <p className="text-[11px] text-slate-500 leading-tight truncate">
                                            #{String(order.order_number).padStart(6, '0')}
                                            {order.title && <> · {order.title}</>}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                        <span className="text-[11px] text-slate-400 tabular-nums">{relative(updated)}</span>
                                        <ChevronRight className="h-4 w-4 text-slate-400" />
                                    </div>
                                </button>
                            </li>
                        );
                    })}
                </ul>
                {changed.length > 2 && (
                    <button
                        type="button"
                        onClick={() => setExpanded(v => !v)}
                        className="w-full px-4 py-2 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 border-t border-slate-100 transition-colors"
                        data-testid="activity-toggle"
                    >
                        {expanded
                            ? t('activity.show_less', 'Show less')
                            : t('activity.show_more', 'Show {n} more', { n: changed.length - 2 })}
                    </button>
                )}
                </>
            )}
        </Card>
    );
};

export default CustomerActivityFeed;
